import { useEffect, useState, useCallback } from 'react';
import { DEFAULT_VISIBLE_BANDS } from '../lib/bands.js';
import { ALL_MODE_BUCKETS, bucketForMode } from '../lib/modes.js';

const KEY = 'geuspider:settings:v2';

// Maps the granular VHF+/MF tokens we used briefly into the collapsed
// LF/VHF/UHF/SHF scheme. Used at load() time only.
const LEGACY_BAND_MAP = {
  '2200': 'LF', '630': 'LF',
  '2': 'VHF',
  '70cm': 'UHF', '23cm': 'UHF', '13cm': 'UHF',
  '6cm': 'SHF', '3cm': 'SHF',
};

// Settings live entirely in localStorage. Bumping the major version (v2) on
// the storage key invalidates older payloads — for the v1→v2 migration we
// just fall back to defaults; users re-pick their filters once.
export const DEFAULTS = Object.freeze({
  callsign: '',                 // optional QRZ-side label only
  locator: 'JN35UH',
  radiusKm: 10000,
  rowLimit: 100,
  theme: 'dark',

  // Quick filters — empty array means "no restriction".
  bands:             [],  // e.g. ['20', '40']
  modes:             [],  // e.g. ['CW', 'FT8']
  dxContinents:      [],  // restrict by DX continent
  spotterContinents: [],  // restrict by spotter continent
  dxCallFilter:      '',  // substring-match against dx_callsign (uppercased)

  // Which band chips appear in the top toolbar. MF/LF and UHF+ bands
  // are excluded by default so the mobile chip row stays manageable.
  visibleBands: [...DEFAULT_VISIBLE_BANDS],

  // Layered list filters. Each entry is a callsign/prefix prefix-match.
  showOnly:    [],
  hide:        [],
  alerts:      [],

  // Per-list on/off so users can disable without losing the entries.
  showOnlyOn: true,
  hideOn:     true,
  alertsOn:   true,

  // Map: draw great-circle line between spotter and DX for each spot.
  showLinks: false,
  // Map: continuously refit the radius to enclose all visible spots.
  // Disabled automatically when the user manually zooms (wheel/pinch/±).
  autoFit: false,
  // Map: render azimuth degree labels (every 30°) around the disc rim.
  showDegrees: false,
});

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    // One-shot migration: the old `continents` field was a single
    // either-side filter; carry the user's selection into the new
    // `dxContinents` axis so they don't lose their EU/NA pick.
    if (Array.isArray(parsed.continents) && parsed.continents.length > 0
        && !Array.isArray(parsed.dxContinents)) {
      parsed.dxContinents = parsed.continents;
    }
    delete parsed.continents;
    // Legacy `modes` held granular tokens (FT8, SSB, RTTY…). Collapse
    // any non-bucket entries into their CW/Phone/Digi bucket.
    if (Array.isArray(parsed.modes) && parsed.modes.length > 0) {
      const bucketSet = new Set(ALL_MODE_BUCKETS);
      const out = new Set();
      for (const m of parsed.modes) {
        if (bucketSet.has(m)) out.add(m);
        else {
          const b = bucketForMode(m);
          if (b) out.add(b);
        }
      }
      parsed.modes = [...out];
    }
    // Legacy granular VHF+ band tokens collapse into LF/VHF/UHF/SHF
    // buckets. Applied to both the active filter (`bands`) and the
    // toolbar visibility list (`visibleBands`).
    const bandMigrate = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return arr;
      const out = new Set();
      for (const b of arr) out.add(LEGACY_BAND_MAP[b] || b);
      return [...out];
    };
    parsed.bands = bandMigrate(parsed.bands);
    parsed.visibleBands = bandMigrate(parsed.visibleBands);
    // Theme set is deliberately small. Anything outside it (e.g. the
    // brief "amber" experiment) reverts to the dark default.
    if (parsed.theme && !['dark', 'light'].includes(parsed.theme)) {
      parsed.theme = 'dark';
    }
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function useSettings() {
  const [settings, setSettings] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // localStorage full / disabled — soldier on with in-memory state.
    }
  }, [settings]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setSettings({ ...DEFAULTS }), []);

  // export() returns the current settings as a JSON string.
  const exportJSON = useCallback(() => {
    return JSON.stringify(settings, null, 2);
  }, [settings]);

  // import_(text) merges a previously-exported JSON dump over defaults.
  // Returns true on success, false on parse error.
  const import_ = useCallback((text) => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object') return false;
      setSettings({ ...DEFAULTS, ...parsed });
      return true;
    } catch {
      return false;
    }
  }, []);

  return [settings, update, reset, { exportJSON, import: import_ }];
}
